import http from "@/services/http";
import type {
  Deposit,
  PaginatedOperations,
  OperationListParams,
  ConfirmOperationRequest,
  AdminProcessRequest,
  OperationNote,
  CreateNoteRequest,
} from "@/models/operation.model";
import { normalizeDepositNote } from "@/models/operation.model";

class DepositService {
  private buildNotePayload(data: CreateNoteRequest): FormData | { content: string } {
    const content = data.content?.trim();
    if (data.image) {
      const formData = new FormData();
      if (content) {
        formData.append("content", content);
      }
      formData.append("image", data.image);
      return formData;
    }

    return { content: content || "" };
  }

  async getAll(params: OperationListParams = {}): Promise<PaginatedOperations<Deposit>> {
    return http.get<PaginatedOperations<Deposit>>("/deposits", params);
  }

  async getAvailable(params: OperationListParams = {}): Promise<PaginatedOperations<Deposit>> {
    return http.get<PaginatedOperations<Deposit>>("/provider/deposits", {
      ...params,
      view: "Available",
    });
  }

  async getMine(params: OperationListParams = {}): Promise<PaginatedOperations<Deposit>> {
    return http.get<PaginatedOperations<Deposit>>("/provider/deposits", {
      ...params,
      view: "Mine",
    });
  }

  async getById(id: string): Promise<Deposit> {
    return http.get<Deposit>(`/deposits/${id}`);
  }

  async take(depositId: string): Promise<Deposit> {
    return http.post<Deposit>(`/provider/deposits/${depositId}/take`);
  }

  async confirm(depositId: string, data: ConfirmOperationRequest): Promise<void> {
    return http.post<void>(`/provider/deposits/${depositId}/confirm`, data);
  }

  async release(depositId: string): Promise<Deposit> {
    return http.post<Deposit>(`/provider/deposits/${depositId}/release`);
  }

  async adminProcess(depositId: string, data: AdminProcessRequest): Promise<Deposit> {
    return http.post<Deposit>(`/admin/deposits/${depositId}/process`, data);
  }

  async adminRelease(depositId: string): Promise<Deposit> {
    return http.post<Deposit>(`/admin/deposits/${depositId}/release`);
  }

  async getNotes(depositId: string): Promise<OperationNote[]> {
    const notes = await http.get<any[]>(`/deposits/${depositId}/notes`);
    return notes.map(normalizeDepositNote);
  }

  async createNote(depositId: string, data: CreateNoteRequest): Promise<OperationNote> {
    const payload = this.buildNotePayload(data);
    const note =
      payload instanceof FormData
        ? await http.postFormData<any>(`/deposits/${depositId}/notes`, payload)
        : await http.post<any>(`/deposits/${depositId}/notes`, payload);
    return normalizeDepositNote(note);
  }

  async markNoteViewed(depositId: string, noteId: string): Promise<void> {
    return http.patch<void>(`/deposits/${depositId}/notes?noteId=${noteId}`);
  }

  async markAllNotesViewed(depositId: string): Promise<void> {
    return http.patch<void>(`/deposits/${depositId}/notes`);
  }
}

export default new DepositService();
